import React, { useCallback, useEffect, useState } from 'react'
import { Card, Space, Button, Form, Input, Modal, type UploadProps, Upload, Select } from 'antd'
import Table, { type ColumnsType } from 'antd/es/table'
import { type AxiosError } from 'axios'
import { setLoader } from 'Store/slices/commonSlice'
import { useAppDispatch } from 'Hooks/ReduxHook'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import { toast } from 'sonner'
import Http, { type ApiErrorData } from 'Util/Http'
import { HTTP_METHOD, type ISong, type IAddSong } from 'Util/Interface'
import dayjs from 'dayjs'

const Songs: React.FC = () => {
  const [tableData, setTableData] = useState<ISong[]>([])
  const [visible, setVisble] = useState(false)
  const [uploadImg, setUploadImg] = useState<any>('')
  const [songImg, setSongImg] = useState<any>('')

  const [form] = Form.useForm()

  const dispatch = useAppDispatch()

  const fetch = useCallback(async () => {
    try {
      dispatch(setLoader(true))
      const response = await Http(`/song`)
      if (response.data?.status && response?.data?.code === 200) {
        dispatch(setLoader(false))
        setTableData(response.data?.data)
      } else {
        setTableData([])
        dispatch(setLoader(false))
      }
    } catch (error: any) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
      setTableData([])
      dispatch(setLoader(false))
    }
  }, [dispatch])

  useEffect(() => {
    void fetch()
  }, [fetch])

  const handleDelete = async (params: ISong) => {
    try {
      const response = await Http(`/song/${String(params.id)}`, {
        method: 'DELETE',
      })
      if (response.data?.status && response?.data?.code === 200) {
        toast.success(response?.data?.message)
        void fetch()
      }
    } catch (error) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
    }
  }

  const handleUpsert = async (param: IAddSong) => {
    try {
      if (param?.song && Number(param?.song[0]?.size) >= 10007822) {
        toast.error('Upload Song File Less Than 10 MB')
        return false
      }

      if (param?.albumArt && Number(param?.albumArt[0]?.size) >= 10007822) {
        toast.error('Upload Album Art File Less Than 10 MB')
        return false
      }

      const formData = new FormData()
      param?.artist && formData.append('artist', param?.artist)
      param?.songName && formData.append('songName', param?.songName)
      param?.songType && formData.append('songType', param?.songType)

      if (param?.albumArt && param?.albumArt[0]?.originFileObj) {
        formData.append('albumArt', param?.albumArt[0]?.originFileObj)
      }

      if (param?.song && param?.song[0]?.originFileObj) {
        formData.append('song', param?.song[0]?.originFileObj)
      }

      const response = await Http({
        url: '/song',
        method: HTTP_METHOD.POST,
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })
      if (response.data?.status && response?.data?.code === 200) {
        toast.success(response?.data?.message)
        form.resetFields()
        setVisble(false)
        setUploadImg('')
        setSongImg('')
        void fetch()
      } else {
        toast.error(response?.data?.message ?? 'Something Went Wrong')
      }
    } catch (error) {
      const err = error as AxiosError<ApiErrorData>
      toast.error(err.response?.data?.message ?? 'Something Went Wrong')
    }
  }

  const handleCancel = () => {
    setVisble(false)
    setUploadImg('')
    setSongImg('')
    form.resetFields()
  }

  const columns: ColumnsType<ISong> = [
    {
      title: 'Id',
      dataIndex: 'index',
      key: 'id',
      render: (value, item, index) => (1 - 1) * 10 + index + 1,
    },
    {
      title: 'Song Name',
      dataIndex: 'songName',
      key: 'songName',
    },
    {
      title: 'Song Type',
      dataIndex: 'songType',
      key: 'songType',
    },
    // {
    //   title: 'Notification Image',
    //   dataIndex: 'notificationImage',
    //   key: 'notificationImage',
    //   render: (value) => {
    //     return (
    //       <>
    //         {value ? (
    //           <Image  src={`${ATTCHMENT_BASE_URL}${String(value)}`} alt="notification" height={100} width={100} />
    //         ) : (
    //           <div style={{ background: 'grey', height: 100, width: 100 }}></div>
    //         )}
    //       </>
    //     )
    //   },
    // },
    {
      title: 'Added Date',
      key: 'created_at',
      dataIndex: 'created_at',
      render: (value) => <>{dayjs(value).format('DD MMM YYYY')}</>,
    },
    {
      title: 'Action',
      dataIndex: 'actions',
      width: '15%',
      render: (_text, record: ISong) => {
        return (
          <Space>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => {
                void handleDelete(record)
              }}
            >
              Delete
            </Button>
          </Space>
        )
      },
    },
  ]

  const props: UploadProps = {
    accept: 'image/png, image/jpeg',
    name: 'thumbnail',
    // listType: 'picture-card',
    className: 'avatar-uploader',
    showUploadList: false,
    maxCount: 1,
    beforeUpload: (file) => {
      const type = file.type.split('/')[1]
      if (Number(file?.size) >= 10007822) {
        toast.error('Upload File Less Than 10 MB')
        return false
      }
      if (type === 'png' || type === 'jpeg' || type === 'jpg') {
        return true
      } else {
        toast.error('Invalid type of file, Accept Only Jpg and Png')
        return false
      }
    },
    customRequest: (info) => {
      setUploadImg(info?.file)
    },
  }

  const songprops: UploadProps = {
    // accept: 'image/png, image/jpeg',
    name: 'thumbnail',
    // listType: 'picture-card',
    className: 'avatar-uploader',
    showUploadList: false,
    maxCount: 1,
    beforeUpload: (file) => {
      if (Number(file?.size) >= 10007822) {
        toast.error('Upload File Less Than 10 MB')
        return false
      }
      // const type = file.type.split('/')[1]
      // if (type === 'png' || type === 'jpeg' || type === 'jpg') {
      //   return true
      // } else {
      //   toast.error('Invalid type of file, Accept Only Jpg and Png')
      //   return false
      // }
    },
    customRequest: (info) => {
      setSongImg(info?.file)
    },
  }

  return (
    <>
      <div className="content-wrapper">
        <Card
          className="card-wrapper"
          title={
            <div className="row justify-between">
              <div>
                <h3>Song List</h3>
              </div>
              <div>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    setVisble(true)
                  }}
                >
                  Add Song
                </Button>
              </div>
            </div>
          }
          style={{ padding: '1.25rem 1.25rem 0' }}
        >
          <Table columns={columns} dataSource={tableData} rowKey={(dataIndex: ISong) => dataIndex?.id} scroll={{ x: 800 }} bordered size="middle" />
        </Card>
      </div>
      <Modal
        title={'Add Notification'}
        open={visible}
        onCancel={handleCancel}
        footer={[
          <Space key={'action'}>
            <Button type="primary" onClick={form.submit} className="btn-padding">
              Save
            </Button>
            <Button danger onClick={handleCancel}>
              Cancel
            </Button>
          </Space>,
        ]}
        width={600}
      >
        <Form
          form={form}
          labelCol={{ span: 7 }}
          wrapperCol={{ span: 14 }}
          autoComplete="off"
          onFinish={(param: IAddSong) => {
            void handleUpsert(param)
          }}
          requiredMark="optional"
        >
          <Form.Item
            label="Artist Name"
            name="artist"
            rules={[
              {
                required: true,
                message: 'Please Enter Artist Name!',
                type: 'string',
              },
            ]}
          >
            <Input className="custom-input" placeholder="Enter Artist Name" />
          </Form.Item>

          <Form.Item
            label="Song Name"
            name="songName"
            rules={[
              {
                required: true,
                message: 'Please Enter Song Name!',
                type: 'string',
              },
            ]}
          >
            <Input className="custom-input" placeholder="Enter Song Name" />
          </Form.Item>

          <Form.Item
            label="Song Type"
            name="songType"
            rules={[
              {
                required: true,
                message: 'Select Song Type!',
              },
            ]}
          >
            <Select
              placeholder="Select Song Type"
              className="custom-select"
              options={[
                { value: 'bhajan', label: 'Bhajan' },
                { value: 'old', label: 'Old Song' },
                { value: 'new', label: 'New Song' },
              ]}
            />
          </Form.Item>

          <Form.Item
            label="Song Image"
            name="albumArt"
            rules={[
              {
                required: true,
                message: 'Please Upload Song Image!',
              },
            ]}
            className="custom-input-label mb-6"
            valuePropName="fileList"
            getValueFromEvent={(event) => {
              return event?.fileList
            }}
          >
            <Upload {...props}>{uploadImg?.name ? <Button>{String(uploadImg?.name)}</Button> : <Button> Upload Image </Button>}</Upload>
          </Form.Item>

          <Form.Item
            label="Song"
            name="song"
            rules={[
              {
                required: true,
                message: 'Please Upload Song!',
              },
            ]}
            className="custom-input-label mb-6"
            valuePropName="fileList"
            getValueFromEvent={(event) => {
              return event?.fileList
            }}
          >
            <Upload {...songprops}>{songImg?.name ? <Button>{String(songImg?.name)}</Button> : <Button> Upload Song </Button>}</Upload>
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default Songs
